<template>
  <div
      class="file-operations-popup"
      :class="{ 'minimized': isMinimized, 'visible': isVisiblePopup }"
  >
    <!-- Заголовок с кнопками управления -->
    <div class="popup-header" @click="toggleMinimized">
      <div class="header-content">
        <VIcon material="file_copy" class="header-icon"/>
        <span class="header-title">
          Операции с файлами ({{ operations.length }})
        </span>
      </div>
      <div class="header-controls">
        <button
            class="control-btn minimize-btn"
            :title="isMinimized ? 'Развернуть' : 'Свернуть'"
        >
          <VIcon
              :material="isMinimized ? 'expand_less' : 'expand_more'"
          />
        </button>
        <button
            class="control-btn close-btn"
            title="Закрыть"
            :disabled="Boolean(activeOperations.length)"
            @click.stop="closePopup"
        >
          <VIcon material="close"/>
        </button>
      </div>
    </div>

    <!-- Содержимое операций -->
    <div v-if="!isMinimized" class="popup-content">
      <!-- Активные операции -->
      <div
          v-if="Utils.isNotEmptyArray(operations)"
          class="operations-section"
      >
        <div
            v-for="operation in operations"
            :key="operation.id"
            class="operation-item"
            :class="{
              'completed': operation.isSuccessfullyCompleted,
              'error': operation.isError,
              'cancelled': operation.isCancelled,
            }"
        >
          <div class="operation-info">
            <div class="operation-header">
              <VIcon :material="operation.operationIcon" class="operation-type-icon"/>
              <span class="file-name" :title="operation.fileName">
                {{ operation.fileName }}
              </span>
              <span class="entity-name" v-if="operation.entityName">
                → {{ operation.entityName }}
              </span>

              <template v-if="operation.isCompleted">
                <span class="mds-badge">
                  <template v-if="operation.isSuccessfullyCompleted">Скачано</template>
                  <template v-if="operation.isError">Ошибка</template>
                  <template v-if="operation.isCancelled">Отменено</template>
                </span>
              </template>
            </div>

            <div class="progress-container">
              <div class="progress-bar">
                <div
                    class="progress-fill"
                    :style="{ width: operation.progress + '%' }"
                    :class="{
                      'error': operation.isError,
                      'cancelled': operation.isCancelled,
                    }"
                ></div>
              </div>
              <div v-if="!operation.isCompleted" class="progress-text">
                {{ operation.formattedProgress }}
                <span v-if="operation.formattedRemainingTime" class="remaining-time">
                  осталось {{ operation.formattedRemainingTime }}
                </span>
              </div>
            </div>
          </div>

          <div
              v-if="operation.canBeCancelled() && canShowCancelButton(operation)"
              class="operation-action"
          >
          <VButton
              class="action-btn cancel-btn"
              title="Отменить операцию"
              theme="link"
              @click="cancelOperation(operation.id)"
          >
            <VIcon material="close"/>
          </VButton>
          </div>
        </div>
      </div>

      <!-- Пустое состояние -->
      <div v-if="hasOperations === false" class="empty-state">
        <VIcon material="inbox" class="empty-icon"/>
        <div class="empty-text">Нет активных операций</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./FilesOperationsPopup.scss"/>

<script>
import VIcon     					 from '@/lib/components/VIcon/VIcon.vue';

import FilesService from '@/core/services/Files/FilesService';
import Utils        from '@/core/utils/Utils';

export default defineComponent({
  name: 'FilesOperationsPopup',
	components: {
		VIcon
	},
  data() {
    return {
      FilesService,
      Utils,
      isMinimized:   false,
      autoHideTimer: null,
    };
  },
  computed: {
    hasOperations() {
      return FilesService.operations.size > 0;
    },
    operations() {
      return Array.from(FilesService.operations.values());
    },
    activeOperations() {
      const result = [];
      for (const operation of FilesService.operations.values()) {
        if (operation.isActive) { result.push(operation); }
      }
      return result;
    },
    completedOperations() {
      const result = [];
      for (const operation of FilesService.operations.values()) {
        if (operation.isCompleted) { result.push(operation) }
      }
      return result;
    },
    // FIXME Клод стражно переживаем про сайд эффекты но я решил пока не переделывать, все таки PoC
    /**
     * Показывать popup если есть операции или недавно были
     */
    isVisiblePopup() {
      const hasActiveOperations = Utils.isNotEmptyArray(this.activeOperations);
      // Если есть активные - показываем и отменяем таймер
      if (hasActiveOperations) {
        this.clearAutoHideTimer();
        return true;
      }
      // Если есть только завершённые операции - показываем
      // (таймер запустится через watch, не здесь!)
      return this.hasOperations;
    }
  },
  watch: {
    'activeOperations.length'(newCount, oldCount) {
      //    Если операции завершились - запускаем таймер автоскрытия
      if (oldCount > 0 && newCount === 0) {
        this.startAutoHideTimer();
      }
      // Если появились новые операции - отменяем автоскрытие
      if (newCount > 0) {
        this.clearAutoHideTimer();
      }
    }
  },
  beforeUnmount() {
    this.clearAutoHideTimer();
  },
  methods: {
    toggleMinimized() {
      this.isMinimized = !this.isMinimized;
    },
    closePopup() {
      // Очищаем завершенные операции и скрываем popup
      this.completedOperations.forEach(op => {
        FilesService.removeOperation(op.id);
      });
      this.clearAutoHideTimer();
    },
    cancelOperation(operationId) {
      FilesService.cancelOperation(operationId);
    },
    removeOperation(operationId) {
      FilesService.removeOperation(operationId);
    },
    /**
     * Показывать кнопку отмены только для больших файлов
     */
    canShowCancelButton(operation) {
      return (operation.fileSize || 0) >= FilesService.config.operationsCancellableSize;
    },
    getStatusText(status) {
      const statusTexts = {
        completed: 'Завершено',
        error:     'Ошибка',
        cancelled: 'Отменено'
      };
      return statusTexts[status] || status;
    },
    startAutoHideTimer() {
      this.clearAutoHideTimer();
      this.autoHideTimer = setTimeout(() => {
        this.closePopup();
      }, FilesService.config.operationsPopupAutoHide);
    },
    clearAutoHideTimer() {
      if (this.autoHideTimer) {
        clearTimeout(this.autoHideTimer);
        this.autoHideTimer = null;
      }
    }
  }
});
</script>
